import { haversineKm } from "../conditions/haversine";
import type { PlannerNode } from "./graph-types";
import { venueOpeningDuring, type VenueOpeningState, type GoogleOpeningHours } from "./venue-hours";

export const PLACE_SOURCE_SELECT = "id, google_place_id, osm_id, source, source_url, source_updated_at, details_fetched_at, nearest_node_id, category, name, lat, lng, elevation_m, price_level, rating, cuisine, opening_hours, reservable, google_maps_uri";
export const VENUE_HOURS_MAX_AGE_MS = 30 * 86_400_000;
export const MAX_VENUE_ACCESS_METRES = 75;
export const FOOD_PLACE_CATEGORIES = new Set(["restaurant", "mountain_restaurant", "cafe", "bar", "apres"]);
export const AMENITY_PLACE_CATEGORIES = new Set(["toilets", "drinking_water", "bike_repair", "shelter", "parking"]);
export type PlaceSourceEvidence = {
  source?: "openstreetmap";
  sourceUrl?: string | null;
  sourceCheckedAt?: string | null;
  sourceUpdatedAt?: string | null;
  openingState?: VenueOpeningState;
  accessDistanceM?: number;
  accessMinutes?: number;
};
export type ReusablePlace = Required<Pick<PlaceSourceEvidence, "source" | "sourceUrl" | "sourceCheckedAt" | "sourceUpdatedAt">> & {
  id: string; sourceId: string; category: string; name: string; lat: number; lng: number;
  elevationM: number | null; cuisine: string | null; openingHours: GoogleOpeningHours;
};
const object = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === "object" && !Array.isArray(value);
const timestamp = (value: unknown) => typeof value === "string" && value.length <= 50 && Number.isFinite(Date.parse(value)) ? value : null;
const coordinate = (value: unknown, limit: number) => (typeof value === "number" || typeof value === "string" && value.trim() !== "") && Number.isFinite(Number(value)) && Math.abs(Number(value)) <= limit ? Number(value) : null;

/** The map and reusable journeys use licensed OSM facts. Legacy Google rows
 * remain a separate provider cache and cannot leak through this adapter. */
export function reusablePlace(raw: unknown): ReusablePlace | null {
  if (!object(raw) || raw.source !== "openstreetmap" || typeof raw.id !== "string" || raw.id.length > 200
    || typeof raw.osm_id !== "string" || !/^(node|way|relation)\/[1-9]\d{0,19}$/.test(raw.osm_id)
    || typeof raw.category !== "string" || !FOOD_PLACE_CATEGORIES.has(raw.category) && !AMENITY_PLACE_CATEGORIES.has(raw.category)) return null;
  const lat = coordinate(raw.lat, 90), lng = coordinate(raw.lng, 180);
  if (lat === null || lng === null) return null;
  const elevationM = raw.elevation_m == null ? null : coordinate(raw.elevation_m, 9000);
  return {
    id: raw.id, sourceId: `openstreetmap:${raw.osm_id}`, source: "openstreetmap",
    sourceUrl: `https://www.openstreetmap.org/${raw.osm_id}`,
    sourceCheckedAt: timestamp(raw.details_fetched_at), sourceUpdatedAt: timestamp(raw.source_updated_at),
    category: raw.category, name: typeof raw.name === "string" ? raw.name.slice(0, 300) : "",
    lat, lng, elevationM, cuisine: typeof raw.cuisine === "string" ? raw.cuisine.slice(0, 300) : null,
    openingHours: object(raw.opening_hours) && raw.opening_hours.source === "openstreetmap" && (raw.opening_hours.raw === null || typeof raw.opening_hours.raw === "string" && raw.opening_hours.raw.length <= 1000) ? { source: "openstreetmap", raw: raw.opening_hours.raw } : null,
  };
}

export function venueHoursCurrent(evidence: PlaceSourceEvidence, now = Date.now()): boolean {
  const checked = evidence.sourceCheckedAt ? Date.parse(evidence.sourceCheckedAt) : NaN;
  return Number.isFinite(checked) && checked <= now + 300_000 && now - checked <= VENUE_HOURS_MAX_AGE_MS;
}

export function assessedVenueOpening(place: PlaceSourceEvidence & { openingHours: unknown }, weekday: number, startHour: number, endHour: number, now = Date.now()): VenueOpeningState {
  if (place.source === "openstreetmap" && !venueHoursCurrent(place, now)) return "unknown";
  return venueOpeningDuring(place.openingHours, weekday, startHour, endHour);
}

/** Nearness is not proof of a connecting path. Only a short estimated local
 * access is included, never a distant or stale stored-node association. */
export function nearestVenueAccess(place: Pick<ReusablePlace, "lat" | "lng" | "elevationM">, nodes: readonly PlannerNode[]): { nodeId: string; accessDistanceM: number; accessMinutes: number } | null {
  let nearest: PlannerNode | null = null, distance = Infinity;
  for (const node of nodes) {
    const metres = haversineKm(place.lat, place.lng, node.lat, node.lng) * 1000;
    if (Number.isFinite(metres) && metres < distance) { distance = metres; nearest = node; }
  }
  if (!nearest || distance > MAX_VENUE_ACCESS_METRES || place.elevationM !== null && Number.isFinite(nearest.ele) && Math.abs(place.elevationM - nearest.ele) > 20) return null;
  return { nodeId: nearest.id, accessDistanceM: Math.ceil(distance), accessMinutes: distance < 5 ? 0 : 2 * Math.max(1, Math.ceil(distance / 50)) };
}

export function placeEvidenceLabel(evidence: PlaceSourceEvidence): string {
  if (!evidence.sourceCheckedAt) return "Opening times and local access are unconfirmed.";
  const date = new Date(evidence.sourceCheckedAt);
  if (!Number.isFinite(date.getTime())) return "Opening times and local access are unconfirmed.";
  return `Map checked ${date.toISOString().slice(0, 10)}. ${venueHoursCurrent(evidence) ? "Listed hours are not live opening confirmation." : "Opening hours need checking again."}`;
}

export function venueVisitWarnings(place: PlaceSourceEvidence & { name: string | null; priceLevel: number | null }, opening: VenueOpeningState, stopType: string): string[] {
  if (place.source !== "openstreetmap") return [];
  return [
    ...(opening === "unknown" ? [`Opening hours for ${place.name ?? "this stop"} are unconfirmed. Check the venue before relying on it.`] : []),
    ...(stopType === "lunch" && place.priceLevel === null ? [`Menu and prices for ${place.name ?? "this stop"} are unconfirmed. Check that it suits your lunch budget.`] : []),
    `${place.name ?? "This stop"} is near a mapped junction. Local access is unverified${place.accessMinutes ? `; about ${place.accessMinutes} minutes of walking there and back is included` : ""}. Follow local signs.`,
  ];
}
